/* Bezalel — GA4 interaction events. Spec + GA4 key-event steps:
 * `claude/ga4-event-tracking-2026-09-21.md` (project docs).
 *
 * WHY THIS FILE EXISTS AT ALL
 * GA4 enhanced measurement never instruments `tel:` or `mailto:` — its
 * outbound-click tracking fires only for http/https links to another host.
 * Measured 1–20 Sep 2026: zero trackable contact events in twenty days, on a
 * business that runs on phone calls. Structural, not low volume. This closes it.
 *
 * ⚠️ CORRECTED TWICE on 21 Sep, both times by a live browser test. Read this
 *    before touching the loader, because both wrong answers looked right.
 *    (1) "The channel's gtag is sandboxed and unreachable from theme JS."
 *        WRONG — diagnosed by curling the server HTML, where the tag genuinely
 *        is absent. web-pixels-manager injects it at RUNTIME. Never diagnose
 *        tag presence on this store from the server HTML; check `window`.
 *    (2) "So `window.gtag` exists — adopt it." ALSO WRONG, and worse. The
 *        pixel installs a STUB (`function(){g.push(arguments)}`) that only
 *        queues into dataLayer. On the journal articles it never loads gtag.js
 *        at all: 82s after load, no script tag, `google_tag_manager`
 *        undefined, no `config` — everything pushed sits in the queue unsent,
 *        forever, with no error anywhere. Adopting on the stub silently
 *        strands every event, which is precisely the failure this file exists
 *        to prevent.
 *    (3) The thing that actually decides delivery is QUEUE ORDER. An event
 *        pushed to dataLayer with no `config` for the destination ahead of it
 *        is DROPPED, not buffered — proven by pushing two `share` events, then
 *        a config, then a third: only the third ever sent. So the config is
 *        seeded at script load, unconditionally, before anything can fire.
 *    Net: a duplicate `config` is harmless (page_view verified to stay at 1);
 *    a missing one loses events silently. Always seed. Never "adopt".
 *
 * ⚠️ Out of theme.js on purpose — same rationale as age-gate.js. theme.js is one
 *    module with ~70 init*() calls behind a single boot(); anything that throws
 *    ahead of ours takes measurement down silently, and silent measurement loss
 *    is the one failure you cannot detect from the data. No deps, own file,
 *    every handler in its own try/catch.
 * ⚠️ Never send page_view, view_item, add_to_cart, begin_checkout,
 *    add_shipping_info, remove_from_cart or purchase from here — the channel's
 *    pixel owns all seven. This file adds interaction events ONLY; anything it
 *    duplicates is double-counted for the life of the property.
 * ⚠️ Same measurement ID + same first-party domain = the same `_ga` cookie, so
 *    these events stitch into the pixel's sessions. Do not give this a second
 *    measurement ID to "keep it separate" — that splits every session in two.
 * ⚠️ Booking-funnel dates come from theme.js's existing IzyRent detection via
 *    the `stay:dates` event (initStaySeasons). Do NOT poll flatpickr again here;
 *    one detection system, per build-conventions §1.
 */
(function () {
  "use strict";

  var tag = document.currentScript || document.querySelector("[data-ga4-id]");
  var ID = (tag && tag.getAttribute("data-ga4-id")) || "";
  if (!ID) return;

  /* ---- consent -----------------------------------------------------------
     No consent banner is configured on this store today, so the Shopify pixel
     tracks unconditionally and matching that is the correct parity. If the
     Customer Privacy API is ever switched on, honour it: only refuse when it is
     present AND says no. An absent API is not a refusal. */
  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === "function") {
        return cp.analyticsProcessingAllowed() !== false;
      }
    } catch (e) {}
    return true;
  }

  /* ---- lazy gtag ---------------------------------------------------------
     Warmed on the visitor's first interaction of any kind, so the tag is
     already in place by the time they reach a phone number — a `tel:` click can
     navigate before an async script has landed, and a lost event looks exactly
     like an event that never happened. */
  /* Seeded at script load, unconditionally: two array pushes, no network, and
     it guarantees a `config` sits in the queue ahead of any event we can
     possibly fire. Reuses the pixel's own dataLayer and stub when they exist,
     so we share its queue rather than starting a rival one. */
  (function seedConfig() {
    try {
      if (!analyticsAllowed()) return;
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== "function") {
        window.gtag = function () { window.dataLayer.push(arguments); };
      }
      window.gtag("js", new Date());
      window.gtag("config", ID, { send_page_view: false });
    } catch (e) {}
  })();

  /* Proof the channel's REAL tag ran for our id — not merely that a stub is
     sitting on `window.gtag`. `google_tag_manager` only exists once gtag.js has
     executed; the script check confirms it was our measurement id. When this is
     true the channel's script will process our seeded config for us, so there
     is nothing to fetch. When it is false — or merely not true YET — we fetch,
     because a redundant cached script costs a few ms and a missing one costs
     every event on the page. */
  var scriptAdded = false;

  /* P3 (28 Sep, reworked): defer to a channel gtag.js tag already loading in this
     document (live test: ours started at 1510ms, the channel's copy at 1602ms —
     same document, never a sandboxed pixel iframe). The channel's load is
     documented as intermittent, so the deferral is never unconditional: fall
     back to injecting ours on that tag's `error` event or a ~4s timeout. */
  var channelScript = null;   // the channel's own <script>, once seen — never ours
  var fallbackArmed = false;
  var fallbackTimer = null;

  function isOurScript(s) { return !!s && !!s.hasAttribute && s.hasAttribute("data-bezalel-gtag"); }
  function matchesId(s) {
    return !!s && s.tagName === "SCRIPT" && typeof s.src === "string" &&
      s.src.indexOf("googletagmanager.com/gtag/js") > -1 && s.src.indexOf(ID) > -1;
  }

  function injectNow() {
    if (scriptAdded) return;
    scriptAdded = true;
    if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
    try {
      var s = document.createElement("script");
      s.async = true;
      s.setAttribute("data-bezalel-gtag", "");   // lets the observer above tell ours from the channel's
      s.src = "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(ID);
      document.head.appendChild(s);
    } catch (e) { scriptAdded = false; }
  }

  /* Safety net for a channel tag that is present but hasn't run yet: only armed
     once (per element), and only injects if the channel never delivers. */
  function armFallback() {
    if (fallbackArmed || !channelScript) return;
    fallbackArmed = true;
    channelScript.addEventListener("error", injectNow, { once: true });
    fallbackTimer = setTimeout(function () {
      if (typeof window.google_tag_manager === "undefined") injectNow();
    }, 4000);
  }

  Array.prototype.forEach.call(document.scripts, function (s) {
    if (!channelScript && matchesId(s) && !isOurScript(s)) channelScript = s;
  });

  var tagObserver = null;
  if (!channelScript && "MutationObserver" in window && document.head) {
    tagObserver = new MutationObserver(function (records) {
      if (channelScript) return;
      for (var i = 0; i < records.length; i++) {
        var nodes = records[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var n = nodes[j];
          if (matchesId(n) && !isOurScript(n)) {
            channelScript = n;
            if (tagObserver) tagObserver.disconnect();
            return;
          }
        }
      }
    });
    try { tagObserver.observe(document.head, { childList: true }); } catch (e) {}
    window.addEventListener("load", function () {
      setTimeout(function () { if (tagObserver) tagObserver.disconnect(); }, 10000);
    });
  }

  function liveTagPresent() {
    try {
      return typeof window.google_tag_manager !== "undefined" &&
        !!document.querySelector(
          'script[src*="googletagmanager.com/gtag/js"][src*="' + ID + '"]:not([data-bezalel-gtag])'
        );
    } catch (e) { return false; }
  }

  function ensureGtag() {
    if (scriptAdded) return;
    if (!analyticsAllowed()) return;
    if (liveTagPresent()) return;                    // the channel's tag has already run
    if (channelScript) { armFallback(); return; }     // present but not run yet — deferred, safety net armed
    injectNow();
  }

  ["pointerdown", "touchstart", "keydown"].forEach(function (evt) {
    document.addEventListener(evt, ensureGtag, { once: true, passive: true, capture: true });
  });

  /* ---- send --------------------------------------------------------------
     Safe to fire before gtag.js has landed: the config was seeded at load, so
     it sits ahead of this event in the queue and the event sends once the
     script arrives. NOT safe without that seed — see note (3) in the header.
     Deduped on name+method+key within 1s: a submit
     handler and a click handler can both see one interaction. */
  var recent = Object.create(null);

  function send(name, params) {
    try {
      ensureGtag();
      // ⚠️ `method` belongs in the key. Without it the two article share links
      // (WhatsApp then email, neither carrying a link_url) collapsed into one
      // `share` — caught in the 21 Sep browser test.
      var p = params || {};
      var key = [name, p.method || "", p.link_url || p.form_name || p.listing || ""].join("|");
      var now = Date.now();
      if (recent[key] && now - recent[key] < 1000) return;
      recent[key] = now;

      var payload = p;
      payload.source_page = location.pathname;
      payload.page_group = pageGroup();
      if (typeof window.gtag === "function") window.gtag("event", name, payload);
    } catch (e) {}
  }

  /* ---- context -----------------------------------------------------------
     page_group is the fallback for which part of the estate an interaction
     belongs to, and on every surface but the contact page it is the whole
     answer: the restaurant page's phone number is the restaurant's. Derived
     from the path rather than the body class because the body carries suffix
     classes, not page identity. */
  function pageGroup() {
    var p = location.pathname;
    if (p === "/" ) return "home";
    if (p.indexOf("/pages/contact") === 0) return "contact";
    if (p.indexOf("family-restaurant") > -1) return "restaurant";
    if (p.indexOf("farmstay-accommodation") > -1) return "accommodation";
    if (p.indexOf("weddings-events") > -1) return "weddings";
    if (p.indexOf("wine-brandy-tasting") > -1) return "tasting";
    if (p.indexOf("/products/") === 0) return "product";
    if (p.indexOf("/collections") === 0) return "collection";
    if (p.indexOf("/blogs/") === 0) return "journal";
    if (p.indexOf("/pages/") === 0) return "page";
    return "other";
  }

  /* The contact page lists general / restaurant / accommodation numbers side by
     side, so the nearest label is the only thing that separates them. Elsewhere
     there is one contact point per page and page_group answers it. */
  function contactPoint(el) {
    try {
      var item = el.closest && el.closest(".cdet__item");
      var label = item && item.querySelector(".cdet__k");
      if (label && label.textContent) {
        return label.textContent.trim().toLowerCase().replace(/[^a-z]+/g, "_");
      }
    } catch (e) {}
    return pageGroup();
  }

  /* ---- clicks ------------------------------------------------------------
     Delegated on the document and matched by href, not by class: the contact
     details, the restaurant's finding block, the house particulars and the
     venue enquiry each render their own markup, and matching six class names
     would silently miss the seventh someone adds later.

     Share links are deliberately NOT contact clicks. `mailto:?subject=` and
     `wa.me/?text=` in main-article.liquid carry no recipient — they open the
     visitor's own compose window to send the article onward. Counting those as
     enquiries would inflate exactly the number this work exists to measure. */
  document.addEventListener("click", function (ev) {
    try {
      var a = ev.target && ev.target.closest && ev.target.closest("a[href]");
      if (!a) return;
      var href = a.getAttribute("href") || "";
      var text = (a.textContent || "").trim().slice(0, 100);

      if (href.indexOf("tel:") === 0) {
        return send("contact_click", {
          method: "phone", contact_point: contactPoint(a), link_url: href, link_text: text
        });
      }

      if (href.indexOf("mailto:") === 0) {
        var addr = href.slice(7).split("?")[0];
        if (!addr) return send("share", { method: "email" });          // compose-to-share
        return send("contact_click", {
          method: "email", contact_point: contactPoint(a), link_url: "mailto:" + addr, link_text: text
        });
      }

      if (href.indexOf("wa.me") > -1) {
        var num = href.split("wa.me/")[1] || "";
        if (!num || num.charAt(0) === "?") return send("share", { method: "whatsapp" });
        return send("contact_click", {
          method: "whatsapp", contact_point: contactPoint(a), link_url: href.split("?")[0], link_text: text
        });
      }

      if (/maps\.app\.goo\.gl|google\.[a-z.]+\/maps|goo\.gl\/maps/.test(href)) {
        return send("directions_click", { destination: "google_maps", link_url: href, link_text: text });
      }

      /* The in-page directions anchor is intent too — someone reading the
         route is planning a drive, not browsing. Kept distinct so the two
         never get added together by accident. */
      if (href.indexOf("#directions") > -1) {
        return send("directions_click", { destination: "directions_section", link_url: href, link_text: text });
      }
    } catch (e) {}
  }, true);

  /* ---- forms -------------------------------------------------------------
     Split on the hidden `form_type` Shopify writes, not on the element id.
     Both {% form 'contact' %} and {% form 'customer' %} POST to /contact, which
     is why GA4's own form_submit lumped 33 events together with no way to tell
     an enquiry from a newsletter signup.

     ⚠️ The farmstay page has NO form of any kind — its template
     (page.country-house.json) carries no form section, so the only form a
     visitor can reach there is the footer newsletter. The seven "farmstay
     enquiries" read from GA4's undifferentiated form_submit on 7 Sep were
     newsletter signups. Naming these events is what makes that legible. */
  document.addEventListener("submit", function (ev) {
    try {
      var f = ev.target;
      if (!f || f.tagName !== "FORM") return;

      if (f.classList && f.classList.contains("stay__form")) {
        return send("booking_submit", { listing: listingName(), stage: "add_to_cart" });
      }

      var typeInput = f.querySelector('input[name="form_type"]');
      var type = typeInput ? typeInput.value : "";

      if (type === "contact") {
        return send("enquiry_submit", { form_name: formName(f) });
      }
      if (type === "customer") {
        return send("newsletter_signup", { form_name: formName(f) });
      }
    } catch (e) {}
  }, true);

  function formName(f) {
    var id = f.getAttribute("id") || "";
    if (id === "contact-letter-form") return "contact_letter";
    if (id === "venue-enquiry") return "weddings_enquiry";
    if (id === "circle-join-form") return "inner_circle";
    if (f.classList && f.classList.contains("newsletter__form")) return "footer_newsletter";
    if (f.classList && f.classList.contains("cdet__news-form")) return "contact_newsletter";
    return id || "unnamed";
  }

  /* ---- booking funnel ----------------------------------------------------
     The booking itself is already measured: the stay PDP posts a native
     {% form 'product' %} into the cart, so an IzyRent booking becomes an
     ordinary Shopify purchase and shows up item-level in GA4. What was missing
     is everything before it — which is where the drop-off lives. */
  function listingName() {
    try {
      var el = document.querySelector("[data-stay]");
      var h = el && el.querySelector("h1");
      if (h && h.textContent) return h.textContent.trim().slice(0, 100);
    } catch (e) {}
    return location.pathname.split("/").pop() || "unknown";
  }

  var stayStarted = false;
  document.addEventListener("change", function (ev) {
    try {
      var f = ev.target && ev.target.closest && ev.target.closest(".stay__form");
      if (!f || stayStarted) return;
      stayStarted = true;
      send("booking_form_start", { listing: listingName() });
    } catch (e) {}
  }, true);

  /* Dates come from theme.js, which already resolves IzyRent's flatpickr with a
     poll and a MutationObserver text fallback. Listening to its event keeps one
     detection system; if theme.js failed to boot we simply lose this one event
     and every handler above still works. */
  document.addEventListener("stay:dates", function (ev) {
    try {
      var d = (ev && ev.detail) || {};
      send("booking_dates_selected", {
        listing: listingName(), season: d.season || "", pick_source: d.source || "calendar"
      });
    } catch (e) {}
  });

  /* ---- venue date checker (B9, launch review 27 Sep) ----------------------
     The funnel from checking a date to enquiring is the weddings page's key
     signal, and until now it wasn't measured at all. venue-date.liquid's
     initVenueDate (theme.js) dispatches `venue:date` only from its enquiry
     CTA, i.e. once a verdict is already showing — so this also tells us the
     state the couple saw, not just that they picked a date. */
  document.addEventListener("venue:date", function (ev) {
    try {
      var d = (ev && ev.detail) || {};
      send("venue_date_check", { season: d.season || "", state: d.state || "" });
    } catch (e) {}
  });

  /* ---- unsubscribe exit poll (5 Oct 2026) ---------------------------------
     snippets/unsubscribe-poll.liquid sits under Shopify's /account/unsubscribe
     message and dispatches `unsub:reason` on one tap. Anonymous by design: the
     reason code is the only parameter (no email, no free text). */
  document.addEventListener("unsub:reason", function (ev) {
    try {
      var d = (ev && ev.detail) || {};
      send("unsubscribe_reason", { reason: String(d.reason || "unknown").slice(0, 40) });
    } catch (e) {}
  });
})();
